import { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as api from 'app/api';
import { setCookie } from 'app/libs/cookie';
import { generateForm, setFormParam, submitForm } from 'app/libs/form';
import { getSessionItem, removeSessionItem, setSessionItem } from 'app/libs/storageUtils';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';

export const identifyTypes = {
	PHONE: 'PHONE',
	IPIN: 'IPIN',
};

const getTimestamp = () => new Date().getTime();
const setIdentifyState = (state) => setCookie('identify_state', state);
const TEMP_DATA_KEY = 'identify_temp_data';

function useIdentify() {
	const ua = useSsrUserAgent();
	const history = useHistory();
	const iPinEncodeDataRef = useRef();
	const phoneEncodeDataRef = useRef();
	const onSuccessRef = useRef();
	const onFailureRef = useRef();
	const [initLoading, setInitLoading] = useState(false);

	const setGlobalIdentifyCallback = useCallback(() => {
		window.identifySuccessCallback = (jsonStr) => {
			const data = JSON.parse(jsonStr);
			if (onSuccessRef.current) onSuccessRef.current(data);
			window.identifySuccessCallback = null;
		};

		window.identifyFailureCallback = (jsonStr) => {
			const data = JSON.parse(jsonStr);
			if (onFailureRef.current) onFailureRef.current(data);
			window.identifyFailureCallback = null;
		};
	}, []);

	const beforeInit = useCallback(async () => {
		const state = getTimestamp().toString();
		const options = { state, nextPath: `${window.location.pathname}${window.location.search}` };

		try {
			const [iPinRes, phoneRes] = await Promise.all([
				api.getIdentifyKey(identifyTypes.IPIN, options),
				api.getIdentifyKey(identifyTypes.PHONE, options),
			]);
			setIdentifyState(state);
			iPinEncodeDataRef.current = iPinRes.data.encodedData;
			phoneEncodeDataRef.current = phoneRes.data.encodedData;
		} catch (e) {
			console.error(e);
			alert('본인인증 정보 초기화에 실패했습니다.');
		} finally {
			setInitLoading(false);
		}
	}, []);

	const initIdentifyKey = useCallback(
		async ({ onSuccess, onFailure }) => {
			const identifyData = getSessionItem(TEMP_DATA_KEY);
			if (identifyData) {
				removeSessionItem(TEMP_DATA_KEY);

				if (identifyData.errorCode) {
					await onFailure(identifyData);
				} else {
					await onSuccess(identifyData);
				}
			}
			if (!iPinEncodeDataRef.current || !phoneEncodeDataRef.current) {
				await beforeInit();
			}
			onSuccessRef.current = onSuccess;
			onFailureRef.current = onFailure;
		},
		[beforeInit],
	);

	const successIdentify = useCallback(
		(data, nextPath) => {
			setSessionItem(TEMP_DATA_KEY, data);
			const decodePath = decodeURIComponent(nextPath);

			if (ua().isPc) {
				if (window.opener && window.opener.identifySuccessCallback) {
					window.opener.identifySuccessCallback(JSON.stringify(data));
					window.close();
				}
			} else {
				history.replace(decodePath);
			}
		},
		[history, ua],
	);

	const failureIdentify = useCallback(
		(errorCode, nextPath) => {
			alert(`죄송합니다. 문제가 발생했습니다. 에러코드 : ${errorCode}`);
			setSessionItem(TEMP_DATA_KEY, { errorCode });
			const decodePath = decodeURIComponent(nextPath);

			if (ua().isPc) {
				if (window.opener && window.opener.identifyFailureCallback) {
					window.opener.identifyFailureCallback(JSON.stringify({ errorCode }));
					window.close();
				}
			} else {
				history.replace(decodePath);
			}
		},
		[history, ua],
	);

	const applyPhoneIdentify = useCallback(async () => {
		if (!phoneEncodeDataRef.current) {
			alert('잠시후 시도해주세요.');
			return;
		}

		if (ua().isPc) {
			window.open(
				`https://check.namecheck.co.kr/checkplus_new_model4/checkplus.cb?m=checkplusSerivce&EncodeData=${phoneEncodeDataRef.current}`,
				'check',
				'width=500,height=470, scrollbars=no,status=no,toolbar=no,resizable=0,location=no,menu=no',
			);
			setGlobalIdentifyCallback();
		} else {
			const form = generateForm('post', 'https://check.namecheck.co.kr/mobile2/checkplus.cb');
			setFormParam(form, 'm', 'checkplusSerivce');
			setFormParam(form, 'EncodeData', phoneEncodeDataRef.current);
			submitForm(form);
		}
	}, [setGlobalIdentifyCallback, ua]);

	const applyIPinIdentify = useCallback(async () => {
		if (!iPinEncodeDataRef.current) {
			alert('잠시후 시도해주세요.');
			return;
		}

		if (ua().isPc) {
			window.open(
				'',
				'popupIPIN2',
				'width=450,height=550,top=100,left=100, scrollbars=no,status=no,toolbar=no,resizable=0,location=no,menu=no',
			);
			setGlobalIdentifyCallback();

			const form = generateForm('get', 'https://cert.vno.co.kr/ipin.cb', 'popupIPIN2');
			setFormParam(form, 'm', 'pubmain');
			setFormParam(form, 'enc_data', iPinEncodeDataRef.current);
			submitForm(form);
		} else {
			const form = generateForm('post', 'https://cert.vno.co.kr/ipin.cb');
			setFormParam(form, 'm', 'pubmain');
			setFormParam(form, 'enc_data', iPinEncodeDataRef.current);
			submitForm(form);
		}
	}, [setGlobalIdentifyCallback, ua]);

	return {
		initLoading,
		initIdentifyKey,
		applyPhoneIdentify,
		applyIPinIdentify,
		successIdentify,
		failureIdentify,
	};
}

export default useIdentify;
